import React, { useState, useEffect, useMemo } from 'react';
import { Grid } from '@mui/material';
import CustomPaper from '../common/CustomPaper';
import SelectProject from '../Component/SelectProject';
import ItemDatabase from './ItemDatabase';
import { send_component_save_request, send_request } from '../../utils/Request';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import { getCurrentTime } from '../../utils/getCurrentTime';
import useCreateDatabaseRecordStore, { getCreateDatabaseRecordStore } from './useCreateDatabaseRecordStore';
import './CreateDatabaseRecordStyle.css';
import checkKey from '../../utils/CheckKey';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import replacePlaceholderValue from '../../utils/ReplacePlaceholderValue';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import W86PlaceholderTextField from '../W86Components/W86PlaceholderTextField';
import '../../styles/DataTableStyle.css';

const CreateDatabaseRecordTemplate = (props) => {
  const [availablePlaceholders, setAvailablePlaceholders] = useState([]);
  const [availableCols, setAvailableCols] = useState([]);
  const [columns, setColumns] = useState([]);
  const [databaseList, setDatabaseList] = useState([]);
  const [targetDatabaseIdLocal, setTargetDatabaseIdLocal] = useState(null);
  const [checkKeyError, setCheckKeyError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loadingScreen, setLoadingScreen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [description, setDescription] = useState(null);
  const [status, setStatus] = useState(null);

  const store = useMemo(() => getCreateDatabaseRecordStore(props.component_id), [props.component_id]);
  const storeData = store((s) => s.data);
  const storeTargetDatabaseId = store((s) => s.targetDatabaseId);

  useEffect(() => {
    if (props.component_id === undefined) return;

    setLoadingScreen(true);

    // Skip primary component data fetch if already loaded (prefilled from keyed store)
    if (store.getState().loaded) {
      props.onComponentNameChanged(store.getState().name);
      props.onLastModifiedChanged(store.getState().lastModified);
      setLoadingScreen(false);
      props.setChildTemplateLoaded(true);
    }

    // Database list is always needed (even on prefill path)
    send_request('database/get_databases', '', {}, 'GET').then((response) => {
      if (response.data) {
        setDatabaseList(response.data);

        if (store.getState().loaded) return;

        send_request(
          `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
          '',
          {}
        ).then((response) => {
          if (
            response.data &&
            response.data.components &&
            response.data.components[0].componentData
          ) {
            props.onComponentNameChanged(response.data.components[0].componentData.name);
            props.onComponentDescriptionChanged(response.data.components[0].description);
            props.onLastModifiedChanged(response.data.components[0].componentData.lastModified);

            let queryData = response.data.components[0].componentData.data;
            if (!Array.isArray(queryData)) {
              queryData = [];
            }
            store.getState().setCreateDatabaseRecordTemplate(
              response.data.components[0]
            );
            store.setState({ loaded: true });
            setLoadingScreen(false);
            props.setChildTemplateLoaded(true);
          } else {
            store.getState().setCreateDatabaseRecordTemplate(
              response.data.components[0]
            );
            store.setState({ loaded: true });
            setLoadingScreen(false);
            props.setChildTemplateLoaded(true);
          }

        });
      }
    });
  }, []);

  useEffect(() => {
    const unsub = useProjectStore.subscribe(
      (s) => s.selectedPlaceholder,
      (placeholder) => {
        const activeId = useProjectStore.getState().activeComponentId;
        if (activeId !== null && activeId !== props.component_id) return;
        if (placeholder && placeholder.selected) {
          const { style, key } = placeholder.selected;
          insertPlaceholder(style.default, key, style);
        }
      }
    );
    return unsub;
  }, []);

  const insertPlaceholder = (label, text, style) => {
    const placeholders = [...store.getState().placeholders];
    placeholders.push({ label, text, style });
    store.getState().setPlaceholders(placeholders);
  };

  const updateTargetProject = (targetDatabaseId) => {
    send_request(`database/get_columns/${targetDatabaseId}`, '', {}, 'GET').then((response) => {
      if (response.data) {
        let columnsResult = [];
        const currentData = store.getState().data;

        response.data.forEach((column) => {
          const col = currentData.columns.filter((c) => c.columnName === column.name);
          const value = col.length > 0 ? col[0].columnValue : '';
          columnsResult.push({
            columnId: column.columnId,
            columnName: column.name,
            columnType: column.type,
            columnValue: value,
            orderNumber: column.orderNumber,
          });
        });

        store.getState().clearData();

        columnsResult.sort((a, b) => (a.orderNumber > b.orderNumber ? 1 : -1));

        store.getState().setData(columnsResult);
      }
    });
    setIsLoading(false);
  };

  const handleKeyChange = (e) => {
    const placeholderValue = replacePlaceholderValue(e.target.value);
    store.getState().setKey(placeholderValue);

    const currentData = store.getState().data;
    const value = checkKey(placeholderValue, currentData);

    setCheckKeyError(value[0]);
    setErrorMessage(value[1]);
  };

  if (!loadingScreen) {
    const { key } = storeData;
    return (
      <Grid container xs={12}>
        <Grid item xs={12}>
          <CustomPaper className="query-item-paper defaultText">
            <Grid container spacing={2} style={{ marginBottom: 24 }}>
              <Grid
                item
                xs={12}
                sm={12}
                md={3}
                lg={2}
                xl={2}
                className="item-label-create-record text-normal"
                style={{ flexBasis: 'unset' }}
              >
                Placeholder{' '}
              </Grid>
              <Grid item xs={12} sm={12} md={9} lg={10} xl={10}>
                <Grid item xs={12} sm={12} md={8} lg={6} xl={6}>
                  <W86PlaceholderTextField
                    value={key}
                    error={checkKeyError ? true : false}
                    helperText={checkKeyError ? errorMessage : null}
                    placeholderText="Enter placeholder for create table"
                    handleChange={handleKeyChange}
                  />
                </Grid>
              </Grid>
            </Grid>
            <SelectProject
              databaseList={databaseList}
              updateTargetProject={updateTargetProject}
              targetDatabaseId={storeTargetDatabaseId}
              setTargetDatabaseId={(targetDatabaseId) => {
                store.getState().setTargetDatabaseId(targetDatabaseId);
                useProjectStore.setState({ targetDataBaseId: targetDatabaseId });
              }}
              component={props.component}
              isLoading={isLoading}
              setIsLoading={(loading) => {
                setIsLoading(loading);
              }}
              updateDatabaseList={(dbList) => {
                setDatabaseList(dbList);
                store.getState().setDatabaseList(dbList);
              }}
            />
            <p className="query-item-content">
              Configure the values for each column in the new record
            </p>
            {storeData.columns.map((column, index) => (
              <ItemDatabase
                availablePlaceholders={props.availablePlaceholders}
                column={column}
                index={index}
                columns={storeData.columns}
              />
            ))}
          </CustomPaper>
        </Grid>
      </Grid>
    );
  } else {
    return <IndividualComponentLoader />;
  }
};

export default CreateDatabaseRecordTemplate;
